import React from 'react';

interface StellarProofsProps {
  isMobile: boolean;
}

const StellarProofs: React.FC<StellarProofsProps> = ({ isMobile }) => {
  const accounts = [
    {
      title: 'Stellar Issuer Account',
      address: 'GDFPCZY5F4QLZQMB5CRR3LF644AP6R76QFY2CSFCZSCELEW5Z532SU7A',
      url: 'https://stellar.expert/explorer/public/account/GDFPCZY5F4QLZQMB5CRR3LF644AP6R76QFY2CSFCZSCELEW5Z532SU7A',
      description: 'Primary issuance authority for UNYKORN assets on Stellar network'
    },
    {
      title: 'Operational Account',
      address: 'GB73QRFVUQS3TJSRQV2BYNKKVWFSNRR2PZREM6MPIHPV7R4Q7BLTKTQB',
      url: 'https://stellar.expert/explorer/public/account/GB73QRFVUQS3TJSRQV2BYNKKVWFSNRR2PZREM6MPIHPV7R4Q7BLTKTQB',
      description: 'Market maker and operational wallet for liquidity provision'
    }
  ];

  const asset = {
    title: 'UNYKORN Asset',
    code: 'UNYKORN',
    issuer: 'GDFPCZY5F4QLZQMB5CRR3LF644AP6R76QFY2CSFCZSCELEW5Z532SU7A',
    url: 'https://stellar.expert/explorer/public/asset/UNYKORN-GDFPCZY5F4QLZQMB5CRR3LF644AP6R76QFY2CSFCZSCELEW5Z532SU7A',
    description: 'Weight-referenced precious metals asset'
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '100px 20px 60px' : '140px 48px 80px'
    }}>
      <div style={{
        fontSize: isMobile ? '32px' : '48px',
        fontWeight: '300',
        marginBottom: '16px',
        background: 'linear-gradient(180deg, #ffffff 0%, #e5e7eb 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
      }}>
        Stellar Proofs
      </div>

      <div style={{
        fontSize: '16px',
        color: '#9ca3af',
        marginBottom: '48px',
        lineHeight: '1.6',
        maxWidth: '800px'
      }}>
        UNYKORN on Stellar is issued by public accounts. All balances, trustlines, and transfers are independently verifiable on the Stellar public ledger. No trust required—only cryptographic proof.
      </div>

      {/* Issuer & Control Accounts */}
      <div style={{
        padding: isMobile ? '28px' : '40px',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        borderRadius: '16px',
        marginBottom: '32px'
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '600',
          marginBottom: '24px',
          color: '#ffffff'
        }}>
          🔐 Issuer & Control Accounts
        </h2>

        {accounts.map((account, i) => (
          <div key={i} style={{
            padding: '24px',
            background: 'rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            marginBottom: i < accounts.length - 1 ? '20px' : '0'
          }}>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#60a5fa',
              marginBottom: '12px'
            }}>
              {account.title}
            </div>
            <div style={{
              fontSize: '14px',
              color: '#9ca3af',
              marginBottom: '16px',
              lineHeight: '1.6'
            }}>
              {account.description}
            </div>
            <div style={{
              fontFamily: 'monospace',
              fontSize: '13px',
              color: '#e5e7eb',
              padding: '12px',
              background: 'rgba(0, 0, 0, 0.4)',
              borderRadius: '8px',
              marginBottom: '12px',
              wordBreak: 'break-all'
            }}>
              {account.address}
            </div>
            <a
              href={account.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '10px 18px',
                background: 'rgba(59, 130, 246, 0.15)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '8px',
                color: '#60a5fa',
                fontSize: '14px',
                fontWeight: '600',
                textDecoration: 'none',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.25)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
              }}
            >
              View on Stellar Expert →
            </a>
          </div>
        ))}
      </div>

      {/* Asset Proof */}
      <div style={{
        padding: isMobile ? '28px' : '40px',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
        border: '1px solid rgba(16, 185, 129, 0.2)',
        borderRadius: '16px'
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '600',
          marginBottom: '24px',
          color: '#ffffff'
        }}>
          💎 Asset Proof
        </h2>

        <div style={{
          padding: '24px',
          background: 'rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px'
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#10b981',
            marginBottom: '12px'
          }}>
            {asset.code} Asset
          </div>
          <div style={{
            fontSize: '14px',
            color: '#9ca3af',
            marginBottom: '16px',
            lineHeight: '1.6'
          }}>
            {asset.description}
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '120px 1fr',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{ color: '#6b7280', fontSize: '13px' }}>Asset Code:</div>
            <div style={{ fontFamily: 'monospace', fontSize: '13px', color: '#e5e7eb' }}>{asset.code}</div>
            
            <div style={{ color: '#6b7280', fontSize: '13px' }}>Issuer:</div>
            <div style={{
              fontFamily: 'monospace',
              fontSize: '13px',
              color: '#e5e7eb',
              wordBreak: 'break-all'
            }}>
              {asset.issuer}
            </div>
          </div>
          <a
            href={asset.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '10px 18px',
              background: 'rgba(16, 185, 129, 0.15)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '8px',
              color: '#10b981',
              fontSize: '14px',
              fontWeight: '600',
              textDecoration: 'none',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(16, 185, 129, 0.25)';
              e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(16, 185, 129, 0.15)';
              e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
            }}
          >
            View Asset on Stellar Expert →
          </a>
        </div>
      </div>

      {/* Verification Badge */}
      <div style={{
        marginTop: '48px',
        padding: '24px',
        background: 'rgba(59, 130, 246, 0.05)',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        borderRadius: '12px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '14px',
          color: '#60a5fa',
          fontWeight: '600',
          marginBottom: '8px'
        }}>
          ✓ VERIFIED ON STELLAR EXPERT
        </div>
        <div style={{
          fontSize: '13px',
          color: '#9ca3af',
          lineHeight: '1.6'
        }}>
          All accounts and assets are publicly auditable on Stellar's mainnet. No custodial trust required.
        </div>
      </div>
    </div>
  );
};

export default StellarProofs;
